import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'

export default function PrivatlivspolitikPage() {
  return (
    <div className="bg-neutral-950 text-white min-h-screen">
      <SEOHead
        title="Privatlivspolitik — Casper DJ"
        description="Læs om, hvordan Casper DJ behandler og beskytter dine personoplysninger i forbindelse med henvendelser, tilbud og booking af DJ."
      />

      {/* Hero Header */}
      <section className="pt-32 pb-14 md:pt-40 md:pb-18 border-b border-neutral-900 bg-neutral-900/20 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-neutral-900 border border-neutral-800 text-xs text-gold-400 font-semibold tracking-wider uppercase">
            <i className="fa-solid fa-shield-halved text-[11px]" aria-hidden="true" />
            <span>Persondata & GDPR</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white uppercase">
            PRIVATLIVSPOLITIK
          </h1>

          <p className="text-neutral-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Hos Casper DJ tager vi beskyttelsen af dine personoplysninger alvorligt. Her kan du læse om, hvordan vi indsamler, behandler og passer på dine data i overensstemmelse med gældende lovgivning.
          </p>

          <p className="text-xs text-neutral-500 pt-1">
            Senest opdateret: Marts 2026
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Quick info highlight */}
          <div className="p-6 md:p-8 rounded-2xl bg-neutral-900/60 border border-neutral-800 mb-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gold-400 uppercase tracking-wider block">
                Dataansvarlig virksomhed
              </span>
              <h2 className="text-xl font-bold text-white">Casper DJ</h2>
              <p className="text-sm text-neutral-400">
                CVR-nr.: 37688967 • Odense • Kører ud i hele Danmark
              </p>
            </div>
            <div className="flex flex-wrap sm:flex-col gap-2 shrink-0 text-sm">
              <a
                href="mailto:info@casperdj.dk"
                className="inline-flex items-center gap-2 text-neutral-200 hover:text-gold-400 transition-colors font-medium lowercase"
              >
                <i className="fa-solid fa-envelope text-xs text-gold-400" aria-hidden="true" />
                <span>info@casperdj.dk</span>
              </a>
              <a
                href="tel:+4530405006"
                className="inline-flex items-center gap-2 text-neutral-200 hover:text-gold-400 transition-colors font-medium"
              >
                <i className="fa-solid fa-phone text-xs text-gold-400" aria-hidden="true" />
                <span>+45 30 40 50 06</span>
              </a>
            </div>
          </div>

          {/* Legal / Policy Body */}
          <div className="space-y-10 text-neutral-300 leading-relaxed text-sm sm:text-base">
            {/* Section 1 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-building" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  1. Dataansvarlig
                </h3>
              </div>
              <p className="text-neutral-300 mb-3">
                Casper DJ er dataansvarlig for behandlingen af de personoplysninger, som vi modtager fra dig som kunde, potentiel kunde eller besøgende på vores hjemmeside{' '}
                <span className="text-white font-medium">casperdj.dk</span>.
              </p>
              <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800/80 text-xs sm:text-sm text-neutral-400 space-y-1">
                <p><strong className="text-neutral-200">Virksomhedsnavn:</strong> Casper DJ</p>
                <p><strong className="text-neutral-200">CVR-nummer:</strong> 37688967</p>
                <p><strong className="text-neutral-200">Geografisk base:</strong> Odense (dækker hele Danmark)</p>
                <p><strong className="text-neutral-200">E-mail:</strong> info@casperdj.dk</p>
                <p><strong className="text-neutral-200">Telefon:</strong> +45 30 40 50 06</p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-address-card" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  2. Hvilke oplysninger indsamler vi, og hvorfor?
                </h3>
              </div>
              <p className="mb-4">
                Vi indsamler udelukkende de oplysninger, der er nødvendige for at kunne rådgive, afgive tilbud, indgå aftaler og levere den bedst mulige musikalske oplevelse til dit arrangement:
              </p>
              <ul className="space-y-3 pl-2 sm:pl-4">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="text-white">Forespørgsler og tilbud:</strong> Navn, e-mailadresse, telefonnummer, arrangementsdato, type af fest (fx bryllup, firmafest, privatfest, konfirmation), lokation for festen samt eventuelle specifikke ønsker til musik, udstyr eller tidsrum.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="text-white">Booking og kontrakt:</strong> Fuld adresse eller faktureringsadresse, evt. firmanavn og CVR-nummer (for erhvervskunder) samt nødvendige kontaktdata på en kontaktperson på selve arrangementsdagen.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="text-white">Bogføring og regnskab:</strong> Fakturaer, betalingsoplysninger og transaktionshistorik i overensstemmelse med gældende regnskabslovgivning.
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="text-white">Tekniske data:</strong> Generelle tekniske logdata (f.eks. IP-adresse og browsertype) i forbindelse med sikkerhed og stabil drift af hjemmesiden.
                  </div>
                </li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-scale-balanced" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  3. Retsgrundlag for behandlingen
                </h3>
              </div>
              <p className="mb-4">
                Behandlingen af dine personoplysninger sker på baggrund af følgende retsgrundlag i overensstemmelse med EU's persondataforordning (GDPR):
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <span className="text-xs font-semibold text-gold-400 uppercase tracking-wider block mb-1">
                    GDPR art. 6, stk. 1, litra b
                  </span>
                  <h4 className="text-white font-semibold text-sm mb-1.5">Opfyldelse af kontrakt</h4>
                  <p className="text-xs text-neutral-400 leading-relaxed">
                    Behandlingen er nødvendig for at besvare din henvendelse, udarbejde et skræddersyet tilbud samt gennemføre den indgåede booking og levere DJ-ydelsen.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <span className="text-xs font-semibold text-gold-400 uppercase tracking-wider block mb-1">
                    GDPR art. 6, stk. 1, litra c
                  </span>
                  <h4 className="text-white font-semibold text-sm mb-1.5">Retlig forpligtelse</h4>
                  <p className="text-xs text-neutral-400 leading-relaxed">
                    Vi opbevarer regnskabsbilag, fakturaer og betalingsoplysninger for at overholde den danske bogføringslovs krav om 5 års opbevaring.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <span className="text-xs font-semibold text-gold-400 uppercase tracking-wider block mb-1">
                    GDPR art. 6, stk. 1, litra f
                  </span>
                  <h4 className="text-white font-semibold text-sm mb-1.5">Legitime interesser</h4>
                  <p className="text-xs text-neutral-400 leading-relaxed">
                    Vores legitime interesse i at besvare generelle spørgsmål, yde god kundeservice, forbedre vores services samt opretholde IT-sikkerheden på vores hjemmeside.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <span className="text-xs font-semibold text-gold-400 uppercase tracking-wider block mb-1">
                    GDPR art. 6, stk. 1, litra a
                  </span>
                  <h4 className="text-white font-semibold text-sm mb-1.5">Samtykke</h4>
                  <p className="text-xs text-neutral-400 leading-relaxed">
                    I tilfælde hvor vi ønsker at dele stemningsbilleder eller udtalelser fra en fest, indhenter vi altid dit udtrykkelige forudgående samtykke.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  4. Opbevaring og sletning af data
                </h3>
              </div>
              <p className="mb-4">
                Vi opbevarer kun dine personoplysninger, så længe det er strengt nødvendigt til de formål, hvortil de er indsamlet:
              </p>
              <ul className="space-y-2.5 pl-2 sm:pl-4 text-neutral-300">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-angle-right text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <span>
                    <strong className="text-white">Uforpligtende forespørgsler:</strong> Forespørgsler og tilbud, der ikke fører til en bindende booking, slettes som udgangspunkt inden for 6-12 måneder.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-angle-right text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <span>
                    <strong className="text-white">Kunder og gennemførte bookinger:</strong> Fakturaer og regnskabsmateriale opbevares i 5 år plus indeværende regnskabsår jf. Bogføringslovens § 10, hvorefter de slettes automatisk.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-angle-right text-xs text-gold-400 mt-1.5 shrink-0" aria-hidden="true" />
                  <span>
                    <strong className="text-white">Løbende korrespondance:</strong> E-mails og beskeder slettes løbende, når arrangementsforløbet er afsluttet, og der ikke længere foreligger et sagligt formål med opbevaringen.
                  </span>
                </li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-lock" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  5. Videregivelse til tredjeparter og databehandlere
                </h3>
              </div>
              <p className="mb-3">
                Vi sælger, udlejer eller overdrager <strong className="text-white">aldrig</strong> dine personlige oplysninger til uvedkommende tredjeparter til markedsføringsformål.
              </p>
              <p className="mb-3">
                Vi anvender betroede underleverandører og databehandlere til f.eks. webhosting, drift af kontaktformularer, e-mailtjenester og regnskabsprogrammer. Disse databehandlere behandler udelukkende personoplysninger efter vores instruks og er underlagt skriftlige databehandleraftaler (DPA), der sikrer, at dine data behandles med samme høje sikkerhedsniveau inden for EU/EØS.
              </p>
            </div>

            {/* Section 6 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-cookie-bite" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  6. Cookies og teknisk funktionalitet
                </h3>
              </div>
              <p>
                Vores hjemmeside er opbygget med respekt for dit privatliv. Vi anvender kun teknisk nødvendige elementer, der sikrer, at sidens navigation, kontaktformularer og sikkerhed fungerer optimalt. Vi anvender ikke aggressive tracking-cookies eller tredjeparts annoncenetværk, der sporer din færden på tværs af internettet.
              </p>
            </div>

            {/* Section 7 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-user-shield" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  7. Dine rettigheder som registreret
                </h3>
              </div>
              <p className="mb-4">
                Du har efter databeskyttelsesforordningen en række rettigheder i forhold til vores behandling af dine personoplysninger:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <strong className="text-white text-sm block mb-1">Ret til indsigt</strong>
                  <p className="text-xs text-neutral-400">Du har ret til at få indsigt i de oplysninger, som vi behandler om dig.</p>
                </div>
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <strong className="text-white text-sm block mb-1">Ret til berigtigelse</strong>
                  <p className="text-xs text-neutral-400">Du har ret til at få urigtige oplysninger om dig selv rettet eller opdateret.</p>
                </div>
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <strong className="text-white text-sm block mb-1">Ret til sletning</strong>
                  <p className="text-xs text-neutral-400">I visse tilfælde har du ret til at få slettet oplysninger om dig forud for vores generelle sletningsfrist.</p>
                </div>
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <strong className="text-white text-sm block mb-1">Ret til begrænsning</strong>
                  <p className="text-xs text-neutral-400">Du har ret til at få begrænset behandlingen af dine personoplysninger i bestemte situationer.</p>
                </div>
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <strong className="text-white text-sm block mb-1">Ret til indsigelse</strong>
                  <p className="text-xs text-neutral-400">Du har ret til at gøre indsigelse mod vores ellers lovlige behandling af dine data.</p>
                </div>
                <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800">
                  <strong className="text-white text-sm block mb-1">Ret til dataportabilitet</strong>
                  <p className="text-xs text-neutral-400">Du har ret til at modtage dine personoplysninger i et struktureret og maskinlæsbart format.</p>
                </div>
              </div>
              <p className="text-xs text-neutral-400 mt-4">
                Hvis du ønsker at gøre brug af dine rettigheder, bedes du kontakte os på{' '}
                <a href="mailto:info@casperdj.dk" className="text-gold-400 hover:underline">
                  info@casperdj.dk
                </a>. Vi besvarer din anmodning hurtigst muligt og senest inden for 30 dage.
              </p>
            </div>

            {/* Section 8 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-gavel" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  8. Klage til Datatilsynet
                </h3>
              </div>
              <p className="mb-3">
                Du har ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, vi behandler dine personoplysninger på. Vi opfordrer dig dog altid til først at tage kontakt til os, så vi hurtigt kan finde en løsning sammen.
              </p>
              <div className="p-4 rounded-xl bg-neutral-950/60 border border-neutral-800 text-xs sm:text-sm text-neutral-400 space-y-1">
                <p><strong className="text-neutral-200">Datatilsynet</strong></p>
                <p>Carl Jacobsens Vej 35, 2500 Valby</p>
                <p>Telefon: +45 33 19 32 00 • E-mail: dt@datatilsynet.dk</p>
                <p>
                  Hjemmeside:{' '}
                  <a
                    href="https://www.datatilsynet.dk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 hover:underline"
                  >
                    www.datatilsynet.dk
                  </a>
                </p>
              </div>
            </div>

            {/* Section 9 */}
            <div className="p-6 sm:p-8 rounded-2xl bg-neutral-900/30 border border-neutral-900">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-neutral-800 text-gold-400 flex items-center justify-center shrink-0 text-sm">
                  <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  9. Ændringer i privatlivspolitikken
                </h3>
              </div>
              <p>
                Vi forbeholder os retten til løbende at opdatere denne privatlivspolitik i takt med, at vores forretningsprocesser udvikler sig, eller hvis der sker ændringer i lovgivningen. Den senest opdaterede version vil altid være tilgængelig på denne side.
              </p>
            </div>
          </div>

          {/* Contact CTA Card */}
          <div className="mt-14 p-8 rounded-3xl bg-neutral-900/70 border border-neutral-800 text-center space-y-6">
            <div className="w-14 h-14 rounded-2xl bg-gold-400 text-black flex items-center justify-center mx-auto text-xl shadow-lg">
              <i className="fa-solid fa-comments" aria-hidden="true" />
            </div>
            <div className="space-y-2 max-w-lg mx-auto">
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                Har du spørgsmål til vores privatlivspolitik?
              </h3>
              <p className="text-sm text-neutral-400">
                Du er altid velkommen til at kontakte mig, hvis du har spørgsmål til behandlingen af dine personoplysninger eller ønsker at vide mere om DJ til din næste fest.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <a
                href="tel:+4530405006"
                className="px-6 py-3 rounded-full bg-gold-400 hover:bg-gold-300 text-black font-semibold text-sm transition-colors inline-flex items-center gap-2"
              >
                <i className="fa-solid fa-phone" aria-hidden="true" />
                <span>+45 30 40 50 06</span>
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-3 rounded-full bg-neutral-800 hover:bg-neutral-700 text-white font-semibold text-sm border border-neutral-700 transition-colors inline-flex items-center gap-2"
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                <span>Gå til kontaktsiden</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
